export type Faq = { q: string; a: string };

export function parseFaqs(raw: string): Faq[] {
    return raw
        .split(/^## /m)
        .filter(Boolean)
        .map((block) => {
            const newline = block.indexOf("\n");
            const q = block.slice(0, newline).trim();
            const a = block.slice(newline + 1).trim();
            return { q, a };
        })
        // exclude the "Still have questions?" footer block
        .filter((faq) => faq.q && faq.a && !faq.q.toLowerCase().startsWith("still have"));
}

/**
 * Convert the answer markdown to clean plain text for structured data
 * (JSON-LD). Strips the leading "→" link rows and unwraps markdown links
 * to their visible label so Google never sees raw markup like
 * "→ [Get in touch](/contact)" in an FAQ answer.
 */
export function faqAnswerToText(md: string): string {
    return md
        .split(/\n{2,}/)
        .map((para) => para.replace(/\n/g, " ").trim())
        // drop pure link rows (they start with the → arrow)
        .filter((para) => para && !para.startsWith("→"))
        // unwrap any inline markdown links to just their label text
        .map((para) => para.replace(/\[([^\]]+)\]\([^)]*\)/g, "$1"))
        .join(" ")
        .trim();
}

/** Convert the answer markdown to safe HTML for rendering in FaqSection. */
export function faqAnswerToHtml(md: string): string {
    // Split into paragraphs on blank lines
    const paragraphs = md.split(/\n{2,}/);

    const htmlParagraphs = paragraphs.map((para) => {
        const line = para.replace(/\n/g, " ").trim();

        // Lines that are purely link lines (start with →) become a link-row div
        if (line.startsWith("→")) {
            const links = line
                .replace(/^→\s*/, "")
                .replace(
                    /\[([^\]]+)\]\((\/[^)]*)\)/g,
                    '<a href="$2" class="faq-link">$1</a>',
                );
            return `<div class="faq-links">${links}</div>`;
        }

        // Regular paragraph — convert any inline links
        const withLinks = line.replace(
            /\[([^\]]+)\]\((\/[^)]*)\)/g,
            '<a href="$2" class="faq-link">$1</a>',
        );
        return `<p>${withLinks}</p>`;
    });

    return htmlParagraphs.join("");
}
